import { useCallback, useEffect, useId, useState } from 'react';
import type * as React from 'react';
import { useParams } from 'react-router';
// ⚠️ The `/markdown` subpath, never the package root. The barrel re-exports
// AppConfigModule and the zod env schema, so importing it here would pull
// NestJS and Node-only code into the browser bundle.
import { renderMarkdown } from '@babysesame/shared/markdown';
import { Page, Notice } from '@/components/page';
import { ErrorState, TableSkeleton } from '@/components/states';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { Button } from '@/components/ui/button';
import { FormField, Input } from '@/components/ui/field';
import { Badge } from '@/components/ui/primitives';
import { rawRequest } from '@/api/client';
import { ApiError } from '@/api/errors';
import { notify } from '@/lib/notify';

interface Section {
  heading: string;
  body: string;
}

interface Draft {
  title: string;
  subtitle: string | null;
  lastUpdated: string;
  sections: Section[];
  publishedAt: string | null;
  version: number;
}

const TITLES: Record<string, string> = {
  'privacy-policy': 'Privacy policy',
  'terms-of-service': 'Terms of service',
};

/**
 * Edits a legal document, then publishes it.
 *
 * ⚠️ **Saving is not publishing, and the two buttons are deliberately separate.**
 * A legal document is fetched by Play Console and App Store Connect whenever
 * they choose; if each keystroke went live the public page would change section
 * by section while someone was halfway through rewriting it. Save writes a
 * draft nobody else can see; Publish swaps the whole document at once.
 *
 * The preview renders with the SAME Markdown configuration the server uses to
 * build the store page — imported from `@babysesame/shared`, not reimplemented
 * — so what an admin sees here is what a store reviewer gets.
 */
export function LegalEditorPage(): React.JSX.Element {
  const { slug = 'privacy-policy' } = useParams();
  const titleId = useId();
  const subtitleId = useId();
  const dateId = useId();

  const [draft, setDraft] = useState<Draft | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [preview, setPreview] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    try {
      const response = await rawRequest(`/admin/legal/${slug}/draft`);
      setDraft((await response.json()) as Draft);
      setDirty(false);
    } catch (err) {
      setLoadError(err instanceof Error ? err : new Error('Could not load the document'));
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    void load();
  }, [load]);

  const patch = (change: Partial<Draft>): void => {
    setDraft((current) => (current ? { ...current, ...change } : current));
    setDirty(true);
  };

  const patchSection = (index: number, change: Partial<Section>): void => {
    setDraft((current) =>
      current
        ? {
            ...current,
            sections: current.sections.map((section, i) =>
              i === index ? { ...section, ...change } : section,
            ),
          }
        : current,
    );
    setDirty(true);
  };

  const save = async (): Promise<Draft | null> => {
    if (!draft) return null;
    setSaving(true);

    try {
      const response = await rawRequest(`/admin/legal/${slug}/draft`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: draft.title,
          subtitle: draft.subtitle,
          lastUpdated: draft.lastUpdated,
          sections: draft.sections,
        }),
      });

      const saved = (await response.json()) as Draft;
      setDraft(saved);
      setDirty(false);
      notify.success('Draft saved', 'Not visible to anyone until you publish it');
      return saved;
    } catch (err) {
      notify.error(err, 'The draft was not saved');
      if (err instanceof ApiError) setLoadError(null);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const publish = async (): Promise<void> => {
    setPublishing(true);

    try {
      // ⚠️ Saved first. Publishing promotes what is STORED, so publishing with
      // unsaved edits on screen would put the previous draft live and leave the
      // admin looking at text nobody else has.
      if (dirty && !(await save())) return;

      const response = await rawRequest(`/admin/legal/${slug}/publish`, { method: 'POST' });
      setDraft((await response.json()) as Draft);
      setDirty(false);
      notify.success('Published', 'Live in the app and at the store URL now');
    } catch (err) {
      notify.error(err, 'The document was not published');
    } finally {
      setPublishing(false);
      setConfirmPublish(false);
    }
  };

  if (loadError) return <ErrorState error={loadError} onRetry={() => void load()} />;
  if (loading || !draft) return <TableSkeleton />;

  const storeUrl = `/api/v1/legal/${slug}.html`;

  return (
    <Page
      title={TITLES[slug] ?? 'Legal document'}
      description="Shown in the app, and given to the Play Store and App Store as a public URL."
      action={
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" onClick={() => setPreview((value) => !value)}>
            {preview ? 'Edit' : 'Preview'}
          </Button>
          <Button
            variant="secondary"
            loading={saving}
            disabled={!dirty}
            onClick={() => void save()}
          >
            Save draft
          </Button>
          <Button variant="primary" loading={publishing} onClick={() => setConfirmPublish(true)}>
            Publish
          </Button>
        </div>
      }
    >
      <Notice title="Saving is not publishing">
        Save keeps a draft only you can see. Publish replaces the document in the app and at{' '}
        <a href={storeUrl} target="_blank" rel="noopener noreferrer" className="underline">
          the store URL
        </a>{' '}
        immediately, as one change.
      </Notice>

      <div className="mt-4 flex items-center gap-2 text-xs text-ink-soft">
        <Badge tone={draft.publishedAt ? 'success' : 'neutral'}>
          {draft.publishedAt ? `Published · v${draft.version}` : `Draft · v${draft.version}`}
        </Badge>
        {dirty ? <span>Unsaved changes</span> : null}
      </div>

      {preview ? (
        <PreviewPane draft={draft} />
      ) : (
        <div className="mt-6 max-w-3xl space-y-4">
          <FormField label="Title" htmlFor={titleId} required>
            <Input
              id={titleId}
              value={draft.title}
              onChange={(event) => patch({ title: event.target.value })}
              maxLength={120}
            />
          </FormField>

          <FormField label="Subtitle" htmlFor={subtitleId} hint="The line under the title.">
            <Input
              id={subtitleId}
              value={draft.subtitle ?? ''}
              onChange={(event) => patch({ subtitle: event.target.value })}
              maxLength={300}
            />
          </FormField>

          <FormField
            label="Last updated"
            htmlFor={dateId}
            // Set by hand, so a typo fix does not claim the policy changed.
            hint="Shown as “LAST UPDATED · …”. Change it only when the text meaningfully changes."
            required
          >
            <Input
              id={dateId}
              type="date"
              value={draft.lastUpdated}
              onChange={(event) => patch({ lastUpdated: event.target.value })}
            />
          </FormField>

          <div className="space-y-3 pt-2">
            {draft.sections.map((section, index) => (
              <SectionEditor
                key={index}
                index={index}
                section={section}
                total={draft.sections.length}
                onChange={(change) => patchSection(index, change)}
                onMove={(delta) => {
                  const next = [...draft.sections];
                  const target = index + delta;
                  const a = next[index];
                  const b = next[target];
                  if (!a || !b) return;
                  next[index] = b;
                  next[target] = a;
                  patch({ sections: next });
                }}
                onRemove={() => patch({ sections: draft.sections.filter((_, i) => i !== index) })}
              />
            ))}

            <Button
              variant="secondary"
              onClick={() => patch({ sections: [...draft.sections, { heading: '', body: '' }] })}
            >
              Add section
            </Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmPublish}
        onOpenChange={setConfirmPublish}
        title="Publish this document?"
        confirmLabel="Publish"
        loading={publishing}
        description="It replaces what every user sees in the app and what the app stores fetch, immediately. The current version stays in the history, so you can publish it again to roll back."
        onConfirm={() => void publish()}
      />
    </Page>
  );
}

function SectionEditor({
  index,
  section,
  total,
  onChange,
  onMove,
  onRemove,
}: {
  index: number;
  section: Section;
  total: number;
  onChange: (change: Partial<Section>) => void;
  onMove: (delta: number) => void;
  onRemove: () => void;
}): React.JSX.Element {
  const headingId = useId();
  const bodyId = useId();

  return (
    <div className="rounded-lg border border-line bg-surface p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-ink-soft">Section {index + 1}</span>
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" disabled={index === 0} onClick={() => onMove(-1)}>
            Up
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={index === total - 1}
            onClick={() => onMove(1)}
          >
            Down
          </Button>
          <Button variant="ghost" size="sm" onClick={onRemove}>
            Remove
          </Button>
        </div>
      </div>

      <FormField label="Heading" htmlFor={headingId} required>
        <Input
          id={headingId}
          value={section.heading}
          onChange={(event) => onChange({ heading: event.target.value })}
          maxLength={200}
        />
      </FormField>

      <FormField
        label="Body"
        htmlFor={bodyId}
        hint="Markdown: **bold**, _italic_, [links](https://…) and lists. No HTML."
        required
      >
        <textarea
          id={bodyId}
          value={section.body}
          onChange={(event) => onChange({ body: event.target.value })}
          rows={6}
          maxLength={20000}
          className="w-full rounded-md border border-line bg-surface-sunken p-2 font-mono text-xs"
        />
      </FormField>
    </div>
  );
}

/**
 * ⚠️ Rendered with the shared configuration, which forbids raw HTML — so this
 * is not an injection point despite using `dangerouslySetInnerHTML`, and it
 * shows exactly what the store page will show rather than an approximation.
 */
function PreviewPane({ draft }: { draft: Draft }): React.JSX.Element {
  return (
    <div className="mt-6 max-w-3xl rounded-lg border border-line bg-surface-sunken p-6">
      <h1 className="text-2xl font-semibold text-ink">{draft.title}</h1>
      <p className="mt-1 text-xs uppercase tracking-wide text-ink-soft">
        Last updated · {draft.lastUpdated}
      </p>
      {draft.subtitle ? <p className="mt-3 text-ink-soft">{draft.subtitle}</p> : null}

      <div className="mt-6 space-y-3">
        {draft.sections.map((section, index) => (
          <section key={index} className="rounded-lg border border-line bg-surface p-4">
            <h2 className="mb-2 text-sm font-semibold text-ink">{section.heading}</h2>
            <div
              className="prose-legal text-sm text-ink-soft [&_a]:underline [&_li]:ml-4 [&_li]:list-disc [&_p]:mb-2 last:[&_p]:mb-0"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(section.body) }}
            />
          </section>
        ))}
      </div>
    </div>
  );
}
